import type { LibraryReference } from '@/types/chat';

const DOCUMENT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_REFERENCES = 20;

const pages = (value: unknown): number[] =>
  Array.isArray(value)
    ? [
        ...new Set(
          value.filter(
            (page): page is number => Number.isInteger(page) && page > 0
          )
        ),
      ].sort((a, b) => a - b)
    : [];

/**
 * `libraryReferences` as FSI sends them on the stream's done event and on session
 * history (FSI #1159): the Library Documents a turn searched or read, with the pages
 * read and the pages its answer cites. Ids, names and page numbers only. Anything
 * malformed is dropped rather than guessed.
 */
export function parseLibraryReferences(value: unknown): LibraryReference[] {
  if (typeof value === 'string') {
    try {
      value = value.trim() ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const references: LibraryReference[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const documentId = record.documentId;
    if (typeof documentId !== 'string' || !DOCUMENT_ID.test(documentId)) {
      continue;
    }
    if (references.some(ref => ref.documentId === documentId)) continue;
    references.push({
      documentId,
      name:
        typeof record.name === 'string'
          ? record.name.replace(/\s+/g, ' ').trim()
          : '',
      pagesRead: pages(record.pagesRead),
      cited: pages(record.cited),
    });
    if (references.length >= MAX_REFERENCES) break;
  }
  return references;
}
